// Instead of passing it an empty string as the initial value, pass it " " (blank space). The "wysiwyg" mode will remove the blank space and keep it as "" by default.
// Link the issue https://github.com/nhn/tui.editor/issues/3206

import "@toast-ui/editor/dist/toastui-editor.css"
import "@toast-ui/editor/dist/theme/toastui-editor-dark.css"
import { useEffect, useRef, useState, useSyncExternalStore } from "react"
import { Editor as ToastUiEditor, EditorProps, Viewer, ViewerProps } from "@toast-ui/react-editor"
import { escapeLeadingPlusBullet } from "@/components/ui/editor/escape-leading-plus-bullet"
import { FormError } from "@/components/ui/form"
import { DARK_APPEARANCE_CLASS_NAME } from "@/layouts/appearance-section"
import { cn } from "@/lib/utils"

export const defaultEditorLength = 4500

const TOAST_UI_DARK_THEME_CLASS_NAME = "toastui-editor-dark"

const subscribeToAppearanceChange = (onAppearanceChange: () => void) => {
  const appearanceObserver = new MutationObserver(onAppearanceChange)
  appearanceObserver.observe(document.documentElement, { attributeFilter: ["class"] })

  return () => appearanceObserver.disconnect()
}

const getIsDarkAppearance = () => document.documentElement.classList.contains(DARK_APPEARANCE_CLASS_NAME)

// ToastUI ships its dark theme as rules scoped under .toastui-editor-dark, so the editor has to be
// told about the appearance separately from the rest of the app.
const useIsDarkAppearance = () => useSyncExternalStore(subscribeToAppearanceChange, getIsDarkAppearance)

export const Editor = ({
  name,
  hideModeSwitch = true,
  toolbarItems = [["bold", "italic", "ol", "ul", "indent", "outdent", "link"]],
  initialEditType = "wysiwyg",
  usageStatistics = false,
  onChange,
  maxLength,
  onBlur,
  children,
  className,
  value,
  error,
  ref: forwardedRef,
  ...otherProps
}: EditorProps & {
  error?: string | null
  value?: string | null
  ref?: React.Ref<ToastUiEditor>
}) => {
  const isDarkAppearance = useIsDarkAppearance()
  const internalRef = useRef<ToastUiEditor>(null)
  const alteredValue = value || " "
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const [editor, setEditor] = useState(alteredValue)

  // limit the number of characters
  useEffect(() => {
    const instance = internalRef.current?.getInstance()
    // Escape the leading "+" ToastUI fails to escape (see handleChange) before comparing,
    // otherwise the stored value (escaped) would never match getMarkdown() (unescaped),
    // triggering a spurious setMarkdown() that resets the caret on every keystroke.
    const markdown = escapeLeadingPlusBullet(instance?.getMarkdown() ?? "")
    if (!editor) {
      return
    }

    if (markdown.trim() !== alteredValue.trim()) {
      instance.setMarkdown(alteredValue, false)
    } else if (editor.length > maxLength) {
      const newMarkdown = markdown.slice(0, maxLength)
      instance.setMarkdown(newMarkdown)
    }
  }, [editor, alteredValue, maxLength])

  useEffect(() => {
    const editorRoot = internalRef.current?.getRootElement?.()
    if (!editorRoot) return

    const toolbarButtons = editorRoot.querySelectorAll(".toastui-editor-toolbar-group button")
    toolbarButtons.forEach((button) => {
      button.setAttribute("tabindex", "-1")
    })
  }, [])

  // Toggled on the editor root rather than passed as the `theme` prop, which ToastUI only reads when
  // it constructs the instance - switching appearance would otherwise need a remount and lose the caret.
  useEffect(() => {
    const editorRoot = internalRef.current?.getRootElement?.()
    editorRoot?.classList.toggle(TOAST_UI_DARK_THEME_CLASS_NAME, isDarkAppearance)
  }, [isDarkAppearance])

  const handleChange = () => {
    const instance = internalRef.current?.getInstance()
    // ToastUI's serializer leaves a leading "+ text" bullet unescaped (it escapes "-", "*",
    // ">" and "<" itself), so a line typed as plain text gets reinterpreted as a bullet list
    // when rendered downstream (Viewer/Markdig) and the marker disappears. Escape it here.
    const markdown = escapeLeadingPlusBullet(instance?.getMarkdown() ?? "")
    if (!maxLength || markdown.length <= maxLength) {
      onChange(markdown)
    }
    setEditor(markdown || " ")
  }

  useEffect(() => {
    if (!forwardedRef) return
    if (typeof forwardedRef === "function") forwardedRef(internalRef.current)
    else if ("current" in forwardedRef) forwardedRef.current = internalRef.current
  }, [forwardedRef])

  return (
    <div ref={editorWrapperRef}>
      <div
        className={className}
        data-testid={`${name}-editor`}
        onBlur={(e) => {
          if (editorWrapperRef.current?.contains(e.relatedTarget)) {
            return
          }
          onBlur?.(e)
        }}>
        <div
          className={cn(
            "focus-within:outline-primary-focused rounded-md transition-colors focus-within:outline-2",
            error && "border-critical border-2",
          )}>
          <ToastUiEditor
            height="auto"
            ref={internalRef}
            hideModeSwitch={hideModeSwitch}
            toolbarItems={toolbarItems}
            onChange={handleChange}
            initialEditType={initialEditType}
            usageStatistics={usageStatistics}
            initialValue={alteredValue}
            {...otherProps}
          />
        </div>
        {error && <FormError>{error}</FormError>}
      </div>
      {children}
    </div>
  )
}

export const EditorView = ({ initialValue, name }: ViewerProps) => {
  const isDarkAppearance = useIsDarkAppearance()

  return (
    <div
      className={cn("view-wrapper", isDarkAppearance && TOAST_UI_DARK_THEME_CLASS_NAME)}
      data-testid={`${name}-view`}>
      <Viewer
        initialValue={initialValue}
        linkAttributes={{
          target: "_blank",
          contenteditable: "false",
          rel: "noopener noreferrer",
        }}
      />
    </div>
  )
}
